import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserData } from '../../hooks/useUserData';
import NotifyPermissionPrompt from '../notifyPermission/NotifyPermissionPrompt';
import { getRealEstateStyle, getRealEstateName } from '../../data/realEstate';
import ModalFrame from '../primitives/ModalFrame';
import { HOUSE_ABANDON_DAYS } from '../../domain/house';
import { format_num } from '../../utils/format/format_num';

/** Pearls for letting the bot message you, asked right after a house purchase (server: NOTIFY_REWARDS). */
const HOUSE_NOTIFY_REWARD_PEARLS = 100_000;


interface RealEstatePurchaseSuccessModalProps {
  purchasedRealEstateId: number;
  onGoToHouse: () => void;
}

/** Попап після успішної покупки будинку */
export default function RealEstatePurchaseSuccessModal({
  purchasedRealEstateId,
  onGoToHouse,
}: RealEstatePurchaseSuccessModalProps) {
  const { t } = useTranslation();
  const purchasedStyle = getRealEstateStyle(purchasedRealEstateId);
  const purchasedName = getRealEstateName(purchasedRealEstateId, t);
  // Until the bot has reached the player once, every purchase first asks to let
  // it send reminders — the house is lost after HOUSE_ABANDON_DAYS away.
  const { data: userData } = useUserData();
  const [askNotify, setAskNotify] = useState(() => !userData?.notifications_warmup_granted);
  const [notifyPearls, setNotifyPearls] = useState(0);

  return (
    <ModalFrame
      isOpen={true}
      onClose={onGoToHouse}
      showBackButton={false}
      hideHeader={true}
      closeOnBackdropClick={false}
      variant="dialog"
      className="max-w-[360px] rounded-xl p-xl border border-white-alpha-10"
      backdropClassName="bg-[transparent]"
    >
      <div className="flex flex-col gap-lg">
        {/* Зображення + назва + вітання */}
        <div className="flex items-center gap-[14px]">
          <img
            src={purchasedStyle.image}
            alt={purchasedName}
            className="h-20 w-20 shrink-0 rounded-[14px] object-contain"
          />
          <div className="flex flex-col gap-xs">
            <p className="m-0 [font-family:Lato,sans-serif] text-[13px] text-white-alpha-55">
              {purchasedName}
            </p>
            <p className="m-0 [font-family:Lato,sans-serif] text-[16px] font-bold leading-[1.3]">
              {t("Congratulations on your new sweet home!")}
            </p>
          </div>
        </div>

        {askNotify ? (
          <NotifyPermissionPrompt
            source="house_purchase"
            rewardPearls={HOUSE_NOTIFY_REWARD_PEARLS}
            onGranted={(reward) => setNotifyPearls(reward.pearls)}
            onDone={() => setAskNotify(false)}
          />
        ) : (<>
        {notifyPearls > 0 && (
          <p className="m-0 flex items-center justify-center gap-sm [font-family:Lato,sans-serif] text-[14px] font-bold text-gold">
            🔔 {t("Reminders are on")} · +{format_num(notifyPearls)}
            <img src="/ui/shell-icon.png" alt="" width={16} height={14} />
          </p>
        )}
        <style>{`
          @keyframes onboardingPulse {
            0% { transform: scale(1); box-shadow: 0 0 0 0 var(--color-brand-alpha-50); }
            50% { transform: scale(1.05); box-shadow: 0 0 18px 4px var(--color-brand-alpha-35); }
            100% { transform: scale(1); box-shadow: 0 0 0 0 var(--color-brand-alpha-50); }
          }
        `}</style>
        <button
          onClick={onGoToHouse}
          className="w-full cursor-pointer rounded-md border-none bg-[image:var(--gradient-brand-deep-angled)] px-2xl py-[15px] [font-family:Lato,sans-serif] text-[17px] font-bold text-white [animation:onboardingPulse_1.8s_ease-in-out_infinite]"
        >
          🏠 {t("Enter house")}
        </button>

        {/* Попередження про 30 днів */}
        <div className="rounded-[10px] border border-accent-alpha-35 bg-accent-alpha-10 px-lg py-[10px]">
          <p className="m-0 [font-family:Lato,sans-serif] text-[13px] leading-[1.45] text-real-estate-purchase-success-modal-text">
            ⚠️ {t("Warning: if you are absent from the game for {{days}} days, your house will be lost.", { days: HOUSE_ABANDON_DAYS })}
          </p>
        </div>

        {/* Адмін вирівняє */}
        <div className="rounded-[10px] border border-white-alpha-10 bg-white-alpha-5 px-lg py-[10px]">
          <p className="m-0 [font-family:Lato,sans-serif] text-[12px] leading-[1.45] text-white-alpha-70">
            🔧 {t("If your house spawned in an awkward spot — for example, partially hanging over a cliff or overlapping with other objects — don't worry. The admins regularly review the map and will reposition it if needed. Please allow 1–2 days after purchase.")}
          </p>
        </div>

        {/* Наступний крок — меблі */}
        <div className="flex flex-col gap-md rounded-[10px] border border-brand-alpha-30 bg-[linear-gradient(135deg,var(--color-brand-alpha-10)_0%,var(--color-deep-blue-alpha-10)_100%)] px-lg py-[10px]">
          <p className="m-0 [font-family:Lato,sans-serif] text-[13px] font-bold leading-[1.45]">
            🪑 {t("Next step: furnish your home!")}
          </p>
          <img
            src="/ui/onboarding/step_2_buy_from_shop.webp"
            alt="Buy from shop"
            className="w-full rounded-sm object-cover"
          />
        </div>
        </>)}
      </div>
    </ModalFrame>
  );
}
